import React, { useState, useEffect } from 'react';
import SalesDetailPage from './SalesDetailPage';
import { PACK_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/packsColumnsConfig';
import { buildPacksFilterGroups } from '../constants/packsFiltersConfig';
import { toastError } from '../../../shared/toastHelper';

const axios = require('axios').default;

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  if (activeFilters.event_series_id && activeFilters.event_series_id.length > 0) {
    params.event_series_id = activeFilters.event_series_id;
  }
  if (activeFilters.product && activeFilters.product.length > 0) {
    params.product = activeFilters.product;
  }
  if (activeFilters.kind && activeFilters.kind.length > 0) {
    params.kind = activeFilters.kind;
  }
  if (activeFilters.status && activeFilters.status.length > 0) {
    params.status = activeFilters.status;
  }
  if (activeFilters.unredeemed && activeFilters.unredeemed.length > 0) {
    params.unredeemed = 'true';
  }
  return params;
};

const PACKS_CONFIG = {
  pageTitle: 'Packs',
  pageSubtitle: 'Flex Pack and Season Pass purchasers and their redemption status.',
  sidebarKey: 'sales_packs',
  defaultPreset: 'year_to_date',
  urlFilterIds: ['venue_ownership_id'],
  dateModeOptions: [{ key: 'created_date', label: 'Purchase Date' }],
  defaultSortRules: [{ field: 'created_at', direction: 'desc' }],

  columns: PACK_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingSalesPacks_${teamId}`,
  firstColumnId: 'purchaser',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/dashboard/sales/packs/data.json`,
  detailResponseKey: 'passes',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/dashboard/sales/packs/export`,
  detailErrorMessage: 'Failed to load pack data.',
  detailTableTitle: 'Packs',
  emptyMessage: 'No packs found.',
  searchPlaceholder: 'Search purchasers...',
  paginationLabel: 'packs',
  itemLabel: (count) => `pack${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current) => [
    { label: 'Packs Sold', value: (current.total_packs || 0).toLocaleString() },
    { label: 'Total Redemptions', value: (current.total_redemptions || 0).toLocaleString() },
    { label: 'Unredeemed Remaining', value: (current.unredeemed_remaining || 0).toLocaleString() },
    { label: 'Expiring in 30 Days', value: (current.expiring_30d || 0).toLocaleString() },
  ],
  kpiGridColumns: 4,

  chartColor: '#6A4C93',
  overTimeKey: null,
  salesCardTitle: null,
  topCharts: [],
  groupByOptions: null,
  groupByDefault: null,

  renderCellValue: (col, pack, { formatEventDate }) => {
    switch (col.id) {
      case 'purchaser': return pack.purchaser || '\u2014';
      case 'email': return pack.email || '\u2014';
      case 'series_name': return pack.series_name || '\u2014';
      case 'product_name': return pack.product_name || '\u2014';
      case 'kind': return pack.kind || '\u2014';
      case 'size': return pack.size != null ? pack.size : '\u2014';
      case 'redeemed': return pack.redeemed != null ? pack.redeemed : '\u2014';
      case 'remaining': return pack.remaining != null ? pack.remaining : '\u2014';
      case 'expires_at': return <span className="text-nowrap">{pack.expires_at ? formatEventDate(pack.expires_at) : '\u2014'}</span>;
      case 'created_at': return <span className="text-nowrap">{pack.created_at ? formatEventDate(pack.created_at) : '\u2014'}</span>;
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Packs',
  filterSubtitle: 'Apply filters to narrow pack data.',
};

function ReportingSalesPacksPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team || !team.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/dashboard/sales/packs/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => toastError('Could not load pack filter options. Please refresh to try again.'));
  }, [team && team.id, csrfToken]);

  const configWithFilters = {
    ...PACKS_CONFIG,
    buildFilterGroups: (venueOwnerships) => buildPacksFilterGroups(venueOwnerships, filterOptions),
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingSalesPacksPage;
